import { useCallback, useEffect, useMemo, useState } from "react";
import "./bop020w030.css";
import { useLocation, useNavigate } from "react-router-dom";
import BOP020W030Template from "./bop020w030.template";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  normalizeCategoryLarge,
  normalizeType,
} from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";
import { useMaster } from "../../common/MasterContext";
import type { ReturnTo } from "../navigationState";
import {
  buildExpenseStatusKey,
  type ExpenseStatusEntry,
} from "../../common/expenseStatus";

/**
 * 機能ID: BOP020w030
 * 機能名: 月次集計画面
 */
const BOP020w030 = () => {
  const SUMMARY_CATEGORY = "合計";
  const DIFFERENCE_CATEGORY = "差分";

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const [month, setMonth] = useState(getCurrentMonth);
  const [results, setResults] = useState<any[]>([]);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, execute } = useApiRequest();
  const { categoryLargeList, categorySmallList } = useMaster();
  // 目標限度額の取得は検索処理とは独立させ、ローディングオーバーレイを表示しない
  const { execute: executeTargetLimit } = useApiRequest<ExpenseStatusEntry[]>();
  // キー: 支出|項目（大）|項目（小）、値: 項目管理画面で設定した目標限度額（未設定はnull）
  const [targetLimitMap, setTargetLimitMap] = useState<
    Map<string, number | null>
  >(new Map());

  const loadTargetLimits = useCallback(async () => {
    const result = await executeTargetLimit(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03001/status`,
    );
    // 取得に失敗しても検索自体は利用できるため、通知せず目標限度額を「-」表示のままにする
    if (!result.ok || !Array.isArray(result.data)) {
      console.error("目標限度額の取得に失敗しました");
      return;
    }
    const map = new Map<string, number | null>();
    result.data.forEach((s) => {
      // 行データ側と同じくnormalizeCategoryLargeを通して突き合わせる
      const key = buildExpenseStatusKey(
        normalizeType(s.type),
        normalizeCategoryLarge(s.categoryLarge || ""),
        s.categorySmall || "",
      );
      if (map.get(key) == null) {
        map.set(key, s.targetAmount ?? null);
      }
    });
    setTargetLimitMap(map);
  }, [executeTargetLimit]);

  useEffect(() => {
    loadTargetLimits();
  }, [loadTargetLimits]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleClear = () => {
    setMonth(getCurrentMonth());
    setResults([]); // 検索結果もクリア
    setHasSearched(false);
  };

  const performMonthlySearch = async (targetMonth: string) => {
    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs03001/search`,
      {
        method: "POST",
        body: JSON.stringify({
          month: targetMonth.replace("-", "") || null,
        }),
      },
    );

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    setResults(
      normalizeMonthlyResults(Array.isArray(result.data) ? result.data : []),
    );
    setHasSearched(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await performMonthlySearch(month);
  };

  const normalizeMonthlyResults = (data: any[]) => {
    const grouped = new Map<string, any>();

    data.forEach((row, index) => {
      const categoryLarge =
        row.categoryLarge === SUMMARY_CATEGORY ||
        row.categoryLarge === DIFFERENCE_CATEGORY
          ? row.categoryLarge
          : normalizeCategoryLarge(row.categoryLarge || "");
      const categorySmall = row.categorySmall || "";
      const type = normalizeType(row.type);
      const key = [type, categoryLarge, categorySmall].join("\u0001");
      const amount = Number(row.amount) || 0;

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...row,
          id: row.id ?? `${key}-${index}`,
          type,
          categoryLarge,
          categorySmall,
          amount,
        });
        return;
      }

      const current = grouped.get(key);
      grouped.set(key, {
        ...current,
        amount: (Number(current.amount) || 0) + amount,
      });
    });

    const monthlyResults = Array.from(grouped.values());
    const hasDifference = monthlyResults.some(
      (row) => row.categoryLarge === DIFFERENCE_CATEGORY,
    );

    if (monthlyResults.length > 0 && !hasDifference) {
      monthlyResults.push({
        id: DIFFERENCE_CATEGORY,
        type: "",
        categoryLarge: DIFFERENCE_CATEGORY,
        categorySmall: "",
        amount:
          getTypeTotal(monthlyResults, INCOME_TYPE) -
          getTypeTotal(monthlyResults, EXPENSE_TYPE),
      });
    }

    return sortMonthlyResults(monthlyResults);
  };

  const getTypeTotal = (rows: any[], type: string) => {
    const totalRows = rows.filter(
      (row) =>
        normalizeType(row.type) === type &&
        row.categoryLarge === SUMMARY_CATEGORY,
    );
    const targetRows =
      totalRows.length > 0
        ? totalRows
        : rows.filter(
            (row) =>
              normalizeType(row.type) === type &&
              row.categoryLarge !== SUMMARY_CATEGORY &&
              row.categoryLarge !== DIFFERENCE_CATEGORY,
          );

    return Math.abs(
      targetRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    );
  };

  const getTypeOrder = (row: any) => {
    if (row.categoryLarge === DIFFERENCE_CATEGORY) return 0;
    const type = normalizeType(row.type);
    if (type === INCOME_TYPE) return 1;
    if (type === EXPENSE_TYPE) return 2;
    return 3;
  };

  const getCategoryOrder = (row: any) => {
    if (row.categoryLarge === DIFFERENCE_CATEGORY) return 0;
    if (row.categoryLarge === SUMMARY_CATEGORY) return 1;
    return 2;
  };

  // 汎用マスタ（登録画面・検索画面のプルダウン）に登場する順序をそのまま並び順として使う。
  // マスタ側のキーも行データ側と同じくnormalizeCategoryLargeを通し、
  // 「外食費」等の旧項目名で登録されたマスタ行があっても正規化後の「食費」と一致させる。
  const { categoryLargeOrderMap, categorySmallOrderMap } = useMemo(() => {
    const largeMap = new Map<string, number>();
    categoryLargeList.forEach((item, index) => {
      const key = `${item.type}|${normalizeCategoryLarge(item.category)}`;
      if (!largeMap.has(key)) {
        largeMap.set(key, index);
      }
    });
    const smallMap = new Map<string, number>();
    categorySmallList.forEach((item, index) => {
      const key = `${item.type}|${normalizeCategoryLarge(item.category)}|${item.label}`;
      if (!smallMap.has(key)) {
        smallMap.set(key, index);
      }
    });
    return { categoryLargeOrderMap: largeMap, categorySmallOrderMap: smallMap };
  }, [categoryLargeList, categorySmallList]);

  const getCategoryLargeMasterOrder = (row: any) =>
    categoryLargeOrderMap.get(`${normalizeType(row.type)}|${row.categoryLarge}`) ??
    Number.MAX_SAFE_INTEGER;

  const getCategorySmallMasterOrder = (row: any) =>
    categorySmallOrderMap.get(
      `${normalizeType(row.type)}|${row.categoryLarge}|${row.categorySmall}`,
    ) ?? Number.MAX_SAFE_INTEGER;

  const sortMonthlyResults = (rows: any[]) =>
    [...rows].sort((a, b) => {
      const typeOrder = getTypeOrder(a) - getTypeOrder(b);
      if (typeOrder !== 0) return typeOrder;

      const categoryOrder = getCategoryOrder(a) - getCategoryOrder(b);
      if (categoryOrder !== 0) return categoryOrder;

      const largeOrder =
        getCategoryLargeMasterOrder(a) - getCategoryLargeMasterOrder(b);
      if (largeOrder !== 0) return largeOrder;

      return getCategorySmallMasterOrder(a) - getCategorySmallMasterOrder(b);
    });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      // 同じキーをクリックした場合は方向を反転
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // 異なるキーをクリックした場合は昇順でソート
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  // ソートされたデータを取得
  const getSortedResults = () => {
    if (!sortKey) return sortMonthlyResults(results);

    return [...results].sort((a, b) => {
      const typeOrder = getTypeOrder(a) - getTypeOrder(b);
      if (typeOrder !== 0) return typeOrder;

      let aValue = a[sortKey];
      let bValue = b[sortKey];

      // 金額の場合は数値として比較
      if (sortKey === "amount") {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      // その他の場合は文字列として比較
      else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

      const categoryOrder = getCategoryOrder(a) - getCategoryOrder(b);
      if (categoryOrder !== 0) return categoryOrder;

      return 0;
    });
  };

  // 支出の明細行にのみ目標限度額を付与する（合計・差分・収入行はnull）
  const sortedResults = getSortedResults().map((row) => ({
    ...row,
    targetAmount:
      row.categoryLarge === SUMMARY_CATEGORY ||
      row.categoryLarge === DIFFERENCE_CATEGORY ||
      normalizeType(row.type) !== EXPENSE_TYPE
        ? null
        : (targetLimitMap.get(
            buildExpenseStatusKey(
              EXPENSE_TYPE,
              row.categoryLarge,
              row.categorySmall,
            ),
          ) ?? null),
  }));

  const handleDetailClick = (row: any) => {
    if (row.categoryLarge === DIFFERENCE_CATEGORY) {
      return;
    }

    const detailType = normalizeType(row.type);
    const searchRecord = {
      type: detailType,
      categoryLarge:
        row.categoryLarge === SUMMARY_CATEGORY
          ? ""
          : normalizeCategoryLarge(row.categoryLarge || ""),
      categorySmall:
        row.categoryLarge === SUMMARY_CATEGORY ? "" : row.categorySmall || "",
    };

    const returnTo: ReturnTo = {
      pathname: "/bop020w030",
      month,
      sortKey,
      sortDirection,
    };

    navigate("/bop020w020", {
      state: {
        record: searchRecord,
        month,
        returnTo,
      },
    });
  };

  // 収支一覧画面から「戻る」で復帰した際に、遷移前の年月・並び順で再検索する
  useEffect(() => {
    let ignore = false;

    const state = location.state as
      | {
          month?: string;
          sortKey?: string;
          sortDirection?: "asc" | "desc";
          autoSearch?: boolean;
        }
      | null;
    if (!state?.autoSearch || !state.month) {
      return;
    }
    setMonth(state.month);
    performMonthlySearch(state.month).then(() => {
      if (ignore) {
        return;
      }
      if (state.sortKey) {
        setSortKey(state.sortKey);
        setSortDirection(state.sortDirection || "asc");
      }
    });

    return () => {
      ignore = true;
    };
    // performMonthlySearchは毎レンダー再生成されるが、参照するのはexecute（安定）と
    // 純粋関数のみのため安全
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <BOP020W030Template
      month={month}
      isLoading={isLoading}
      results={sortedResults}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onMonthChange={setMonth}
      onClear={handleClear}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onDetailClick={handleDetailClick}
      onSort={handleSort}
      showResults={hasSearched}
    />
  );
};

export default BOP020w030;
